import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ShieldCheck, Sparkles, RotateCcw } from 'lucide-react';
import API_BASE from '../lib/apiBase';
import { Card } from '../components/ui/card';

const FEATURES = [
  'Unlimited synced storage — no 20 MB cap',
  'No 100-note limit on your canvas',
  'Sync across every device automatically',
  'Priority support from the AnoteQuest team',
];

function formatPrice(pricing) {
  if (!pricing) return null;
  const amount = Number(pricing.amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: pricing.currency || 'USD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
  const unit = (pricing.intervalUnit || 'MONTH').toLowerCase();
  const interval = pricing.intervalCount > 1 ? `${pricing.intervalCount} ${unit}s` : unit;
  return { formatted, interval };
}

export default function Subscribe() {
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [paypalConfig, setPaypalConfig] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?mode=login&next=/subscribe', { replace: true });
      return undefined;
    }

    const loadPaypalConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/paypal/config`);
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result?.clientId || !result?.planId) {
          throw new Error(result.error || 'PayPal checkout is not configured yet. Please try again shortly.');
        }
        setPaypalConfig({ clientId: result.clientId, planId: result.planId, pricing: result.pricing || null });
      } catch (err) {
        setError(err.message || 'PayPal checkout is not configured yet. Please try again shortly.');
        setLoading(false);
      }
    };

    loadPaypalConfig();
  }, [navigate]);

  useEffect(() => {
    if (!paypalConfig) return undefined;
    const token = localStorage.getItem('token');
    const scriptId = 'paypal-subscription-sdk';
    let script = document.getElementById(scriptId);
    const renderButtons = () => {
      if (!window.paypal || !buttonRef.current || buttonRef.current.childElementCount) return;
      setLoading(false);
      window.paypal.Buttons({
        style: { shape: 'pill', color: 'gold', layout: 'vertical', label: 'subscribe' },
        createSubscription: (_data, actions) => actions.subscription.create({ plan_id: paypalConfig.planId }),
        onApprove: async (data) => {
          try {
            const response = await fetch(`${API_BASE}/api/paypal/subscriptions/activate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ subscriptionId: data.subscriptionID }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Could not verify your subscription.');
            localStorage.setItem('anotequest_premium', 'true');
            toast.success('Welcome to AnoteQuest Premium!');
            navigate('/app', { replace: true });
          } catch (err) {
            setError(err.message || 'Your approval was received, but we could not verify it yet. Please contact support if this persists.');
          }
        },
        onError: () => setError('PayPal checkout could not be loaded. Please try again.'),
      }).render(buttonRef.current);
    };

    if (script) {
      script.addEventListener('load', renderButtons);
      renderButtons();
    } else {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(paypalConfig.clientId)}&vault=true&intent=subscription`;
      script.async = true;
      script.addEventListener('load', renderButtons);
      script.addEventListener('error', () => { setError('PayPal checkout could not be loaded. Please try again.'); setLoading(false); });
      document.head.appendChild(script);
    }
    return () => script?.removeEventListener('load', renderButtons);
  }, [navigate, paypalConfig]);

  const price = formatPrice(paypalConfig?.pricing);

  return (
    <main className="min-h-screen bg-background px-4 py-12 text-foreground sm:py-16">
      <div className="mx-auto flex max-w-md flex-col items-center">
        <Link to="/" className="mb-8 flex items-center gap-2 text-lg font-semibold">
          A<span className="text-primary">note</span>Quest
        </Link>

        <Card className="w-full overflow-hidden p-0 shadow-lg">
          <div className="border-b border-border bg-muted/40 px-6 py-6 sm:px-8 sm:py-8">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              AnoteQuest Premium
            </p>
            <div className="mt-3 flex items-end gap-1.5">
              {price ? (
                <>
                  <span className="text-4xl font-semibold tracking-tight">{price.formatted}</span>
                  <span className="pb-1 text-sm text-muted-foreground">/ {price.interval}</span>
                </>
              ) : (
                <span className="text-2xl font-semibold tracking-tight text-muted-foreground">Loading price…</span>
              )}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">More room for your ideas, synced everywhere.</p>
          </div>

          <div className="px-6 py-6 sm:px-8 sm:py-7">
            <ul className="space-y-3">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 min-h-12" ref={buttonRef} />
            {loading && !error && (
              <p className="mt-3 text-center text-sm text-muted-foreground">Loading secure checkout…</p>
            )}
            {error && (
              <p className="mt-3 text-center text-sm text-destructive">{error}</p>
            )}

            <div className="mt-6 flex items-center justify-center gap-4 border-t border-border pt-5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Secure checkout via PayPal
              </span>
              <span className="flex items-center gap-1.5">
                <RotateCcw className="h-3.5 w-3.5" />
                Cancel anytime
              </span>
            </div>
          </div>
        </Card>

        <Link to="/app" className="mt-6 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          Back to your workspace
        </Link>
      </div>
    </main>
  );
}
